import { useEffect, useState } from "react";
import { useSalon } from "../context/SalonContext";

/**
 * TotemScreensaver - Fullscreen idle overlay shown after 45s of inactivity.
 * Touch anywhere closes it (handled by parent IdleTimer reset).
 */
const TotemScreensaver = ({ onWake }) => {
    const { brand, theme } = useSalon();
    const images = brand.totemImages?.length ? brand.totemImages : [brand.heroImage];
    const [idx, setIdx] = useState(0);
    const [now, setNow] = useState(new Date());
    const [slogan, setSlogan] = useState(theme.slogans[0]);

    useEffect(() => {
        const t1 = setInterval(() => {
            setIdx((i) => (i + 1) % images.length);
            setSlogan(theme.slogans[Math.floor(Math.random() * theme.slogans.length)]);
        }, 7000);
        const t2 = setInterval(() => setNow(new Date()), 1000);
        return () => {
            clearInterval(t1);
            clearInterval(t2);
        };
    }, [images.length, theme.slogans]);

    const hh = now.getHours().toString().padStart(2, "0");
    const mm = now.getMinutes().toString().padStart(2, "0");
    const dateStr = now.toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
    });

    return (
        <div
            data-testid="totem-screensaver"
            onClick={onWake}
            onTouchStart={onWake}
            className="fixed inset-0 z-[9990] cursor-pointer select-none overflow-hidden"
            style={{ background: "#000" }}
        >
            {/* slideshow */}
            {images.map((src, i) => (
                <div
                    key={src + i}
                    className="absolute inset-0 transition-opacity duration-[1500ms] ease-in-out"
                    style={{
                        backgroundImage: `url(${src})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        opacity: i === idx ? 1 : 0,
                        filter: "saturate(1.1) contrast(1.05)",
                    }}
                ></div>
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/70"></div>

            {/* center logo + slogan */}
            <div className="relative z-10 h-full w-full flex flex-col items-center justify-center text-center px-8">
                <div
                    className="h-28 w-28 rounded-3xl flex items-center justify-center mb-8 shadow-[0_30px_80px_-10px_rgba(0,0,0,0.7)]"
                    style={{
                        background: `linear-gradient(135deg, ${brand.primaryColor}, ${brand.primaryColor}55)`,
                    }}
                >
                    <i className="fa-solid fa-scissors text-white text-5xl"></i>
                </div>
                <div
                    className="salon-heading text-5xl md:text-7xl text-white tracking-tight"
                    data-testid="totem-shop-name"
                >
                    {brand.shopName}
                </div>
                <div className="mt-4 text-base md:text-lg text-white/70 max-w-2xl salon-body">
                    {slogan}
                </div>

                {/* digital clock */}
                <div className="mt-12 flex items-baseline gap-3 font-mono">
                    <div
                        className="text-7xl md:text-8xl font-bold tabular-nums tracking-tight"
                        style={{ color: brand.primaryColor }}
                        data-testid="totem-clock"
                    >
                        {hh}
                        <span className="opacity-50 animate-pulse">:</span>
                        {mm}
                    </div>
                </div>
                <div className="mt-2 text-sm uppercase tracking-[0.35em] text-white/50">
                    {dateStr}
                </div>

                <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center gap-2">
                    {images.map((_, i) => (
                        <span
                            key={i}
                            className="h-1 rounded-full transition-all"
                            style={{
                                width: i === idx ? "32px" : "12px",
                                background: i === idx ? brand.primaryColor : "rgba(255,255,255,0.3)",
                            }}
                        ></span>
                    ))}
                </div>

                <div className="absolute bottom-3 right-6 text-[10px] uppercase tracking-[0.3em] text-white/40">
                    <i className="fa-solid fa-hand-pointer mr-2"></i>Toque para acordar
                </div>
            </div>
        </div>
    );
};

export default TotemScreensaver;
